import React from 'react'
import styled from 'styled-components'
import Tooltip from '../../../components/Tooltip'
import Loadout from './Loadout.js'
import * as Options from '../Options.js'

const getRosterColor = ({ colors }, marks, player) => {
    const dead = player.status === 'dead' && player.health === 0
    const knocked = player.status !== 'dead' && player.health === 0

    if (knocked) return colors.roster.knocked
    if (dead) return colors.roster.dead
    return colors.roster.enemy
}

const getStatusDotColor = ({ colors }, player) => {
    const dead = player.status === 'dead' && player.health === 0
    const knocked = player.status !== 'dead' && player.health === 0
    if (knocked) return colors.roster.knocked
    if (dead) return colors.roster.dead
    return 'var(--teal-1)'
}

const MAX_NAME_DISPLAY_LENGTH = 30
const formatPlayerName = (name) =>
    name.length > MAX_NAME_DISPLAY_LENGTH
        ? `${name.slice(0, MAX_NAME_DISPLAY_LENGTH)}…`
        : name

const RosterWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`

const TableHeader = styled.div`
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr) 26px 30px 42px;
    gap: 6px;
    padding: 8px 12px 10px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--grey-1);
    border-bottom: 1px solid var(--border);
    margin-bottom: 4px;
`

const TableHeaderCell = styled.span`
    text-align: ${p => (p.align === 'right' ? 'right' : 'left')};
`

const TeamCard = styled.div`
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`

const TeamHeaderBar = styled.button`
    width: 100%;
    padding: 8px 12px;
    margin: 0;
    border: none;
    border-bottom: 1px solid var(--border);
    background: var(--bg-elevated);
    color: var(--grey-1);
    font-size: 0.75rem;
    font-weight: 600;
    text-align: left;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;

    &:hover {
        background: var(--grey-0);
        color: var(--grey-2);
    }

    ${p => p.$allTracked && `
        background: rgba(245, 158, 11, 0.08);
        color: var(--accent);
        border-bottom-color: rgba(245, 158, 11, 0.3);
    `}
`

const ShowAllButton = styled.button`
    width: 100%;
    padding: 8px 12px;
    margin-bottom: 10px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--bg-elevated);
    color: var(--grey-2);
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;

    &:hover {
        background: var(--grey-0);
        color: var(--accent);
    }
`

const PlayerRow = styled.li`
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr) 26px 30px 42px;
    gap: 6px;
    align-items: center;
    margin: 0;
    padding: 10px 12px;
    min-height: 36px;
    box-sizing: border-box;
    cursor: pointer;
    list-style: none;
    border-radius: 6px;
    transition: background 0.15s ease, box-shadow 0.15s ease;
    border-left: 3px solid transparent;

    &:hover {
        background: var(--grey-0);
    }

    ${p => p.$tracked && `
        border-left-color: var(--accent);
        background: rgba(245, 158, 11, 0.06);
        &:hover { background: rgba(245, 158, 11, 0.1); }
    `}
`

const StatusDot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${p => p.$color};
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.2);
`

const PlayerNameCell = styled.div`
    display: flex;
    align-items: center;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    font-weight: 500;
    font-size: 0.95rem;
    white-space: nowrap;
    text-overflow: ellipsis;
`

const StatCell = styled.div`
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-size: 0.9rem;
    color: var(--grey-2);
    opacity: 0.95;
    min-width: 0;
`

const Roster = ({ match, telemetry, marks, rosters }) => {
    const allPlayerNames = rosters.flat()
    const allTracked = allPlayerNames.length > 0 && allPlayerNames.every(name => marks.isPlayerTracked(name))
    const toggleAllNames = () => {
        if (!marks.setTrackedPlayers) return
        if (allTracked) marks.setTrackedPlayers([])
        else marks.setTrackedPlayers(allPlayerNames)
    }

    return (
        <Options.Context.Consumer>
            {({ options }) => (
                <RosterWrapper>
                    {marks.setTrackedPlayers && (
                        <ShowAllButton type="button" onClick={toggleAllNames} title={allTracked ? 'Tümünü kapat' : 'Tümünü göster'}>
                            {allTracked ? 'Tümünü kapat' : 'Tümünü göster'}
                        </ShowAllButton>
                    )}
                    <TableHeader>
                        <span />
                        <TableHeaderCell>Oyuncu</TableHeaderCell>
                        <TableHeaderCell align="right">Kill</TableHeaderCell>
                        <TableHeaderCell align="right">Asist</TableHeaderCell>
                        <TableHeaderCell align="right">Hasar</TableHeaderCell>
                    </TableHeader>
                    {rosters.map((r, teamIdx) => {
                        const teamAllTracked = r.length > 0 && r.every(name => marks.isPlayerTracked(name))
                        return (
                        <TeamCard key={`roster-${teamIdx}-${r.length}`}>
                            <TeamHeaderBar
                                type="button"
                                $allTracked={teamAllTracked}
                                onClick={() => r.length > 0 && marks.toggleTrackedPlayer(...r)}
                                title={teamAllTracked ? 'Takımı takipten çıkar' : 'Tüm takımı takip et'}
                            >
                                <span>Takım ({r.length} oyuncu)</span>
                            </TeamHeaderBar>
                            <ul style={{ margin: 0, padding: '6px 8px' }}>
                                {r.map(playerName => {
                                    const p = telemetry.players[playerName] || {
                                        name: playerName,
                                        kills: 0,
                                        assists: 0,
                                        damageDealt: 0,
                                        status: 'alive',
                                        health: 100,
                                        items: [],
                                        teammates: [],
                                    }
                                    const color = getRosterColor(options, marks, p)
                                    const dotColor = getStatusDotColor(options, p)
                                    const tracked = marks.isPlayerTracked(p.name)

                                    return (
                                        <PlayerRow
                                            key={p.name}
                                            $tracked={tracked}
                                            onClick={() => marks.toggleTrackedPlayer(p.name)}
                                            onMouseEnter={() => marks.setHoveredPlayer(p.name)}
                                            onMouseLeave={() => marks.setHoveredPlayer(null)}
                                            style={{ color }}
                                        >
                                            <StatusDot $color={dotColor} />
                                            <Tooltip
                                                arrow
                                                placement="left"
                                                duration={0}
                                                theme="pubgsh"
                                                html={(
                                                    <div>
                                                        {p.name.length > MAX_NAME_DISPLAY_LENGTH && (
                                                            <div style={{ marginBottom: 6, fontWeight: 600 }}>{p.name}</div>
                                                        )}
                                                        <Loadout items={p.items} />
                                                    </div>
                                                )}
                                            >
                                                <PlayerNameCell title={p.name}>{formatPlayerName(p.name)}</PlayerNameCell>
                                            </Tooltip>
                                            <StatCell>{p.kills}</StatCell>
                                            <StatCell>{p.assists}</StatCell>
                                            <StatCell>{Math.round(p.damageDealt)}</StatCell>
                                        </PlayerRow>
                                    )
                                })}
                            </ul>
                        </TeamCard>
                        )
                    })}
                </RosterWrapper>
            )}
        </Options.Context.Consumer>
    )
}

export default Roster



// WEBPACK FOOTER //
// ./src/components/MatchPlayer/Roster/index.js